/**
 * The one thing standing between a screen that throws and a blank page.
 *
 * This was found rather than anticipated. Driving the signed-in console in a
 * real browser against a server answering one endpoint with the right status
 * and the wrong shape produced a `TypeError` inside a dashboard band, and React
 * did what React does with an uncaught render error: it unmounted the root. Not
 * the band, not the screen, the whole application. What was left was
 * `<div id="root"></div>` and a cream page with nothing on it, no navigation to
 * leave by, and no text saying anything had gone wrong. Nothing in the unit
 * suite could have seen it, because a unit test mounts one screen and a screen
 * that throws in a test is a failing test rather than a blank product.
 *
 * The boundary sits inside the frame, around the routed content, so the shell
 * survives and the navigation still works. Somebody whose Insight screen has
 * broken can still open Cases, which is the difference between a bad afternoon
 * and a support call.
 *
 * ## A class, in a codebase that has none
 *
 * `componentDidCatch` and `getDerivedStateFromError` have no hook equivalent.
 * React has not shipped one, and the community packages are this class with a
 * different name on it. So this is the one class in `src`, and it is here
 * rather than pulled from a dependency because it is thirty lines, and a
 * dependency for thirty lines is a dependency to audit for ever.
 *
 * ## What the fallback says, and what it does not
 *
 * It does not offer to try again in place, because it cannot know whether that
 * would do anything: the failure is in rendering rather than in fetching, and a
 * retry that re-renders the same state is a button that looks broken. It offers
 * the two things that are true, reload and go somewhere else.
 *
 * It does not print the exception. A stack trace tells a member of staff in a
 * field office nothing they can act on, and an error message built by string
 * interpolation can carry a fragment of a record they may not be permitted to
 * see, which on this product means part of somebody's conversation. The
 * exception goes to `console.error`, where a developer looks and where the
 * browser suite reads it.
 *
 * It does not report anywhere yet. There is no error reporting service wired
 * into this console, and `componentDidCatch` below is where one goes.
 */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { EmptyState } from '@/components/data'
import { Button } from '@/components/primitives'
import styles from './ScreenError.module.css'

export interface ScreenErrorBoundaryProps {
  children: ReactNode
  /**
   * What to show instead.
   *
   * A slot rather than a fixed fallback, because the two halves of the route
   * table have different answers. The signed-in console can offer a way back to
   * a screen that works; the sign-in page cannot, since every address it could
   * link to bounces off the guard and returns here.
   */
  fallback: ReactNode
}

interface ScreenErrorBoundaryState {
  failed: boolean
}

export class ScreenErrorBoundary extends Component<
  ScreenErrorBoundaryProps,
  ScreenErrorBoundaryState
> {
  override state: ScreenErrorBoundaryState = { failed: false }

  static getDerivedStateFromError(): ScreenErrorBoundaryState {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    /*
     * The component stack is the half that matters and the half a bare
     * `console.error(error)` throws away: the message says a property was read
     * off undefined, and a stack of transformed frames rarely says which band
     * of which screen was reading it. Whoever wires up error reporting sends
     * both from here.
     */
    console.error('A screen failed to render.', error, info.componentStack)
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

export interface ScreenErrorProps {
  /**
   * A quieter way out, beside the reload: a link back to a screen that works.
   * Omitted where there is nowhere to go.
   */
  action?: ReactNode
}

/**
 * The words a failed screen shows.
 *
 * An `EmptyState`, because that is this library's component for a region with
 * nothing in it and a sentence about what to do next, and a bespoke panel here
 * would be a second visual grammar for the same situation. `role="alert"`
 * because the content region has just changed under somebody who may not have
 * been looking at it.
 */
export function ScreenError({ action }: ScreenErrorProps) {
  return (
    <div className={styles.failure} role="alert">
      <EmptyState
        headline="This screen stopped working"
        body="Something in it could not be drawn, so the rest of the console has been left alone. Nothing you were looking at has been changed. Reloading usually clears it, and if it happens again on the same screen it is a fault worth reporting rather than anything you did."
        action={
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        }
        secondaryAction={action}
      />
    </div>
  )
}
